export type PortalThemeKey = 'dark-blue' | 'dark' | 'gray-black';
export type ThemeVariableKind = 'color' | 'font-family' | 'font-size';

export interface ThemeVariableDefinition {
  name: string;
  label: string;
  kind: ThemeVariableKind;
}

export const THEME_VARIABLE_DEFINITIONS: readonly ThemeVariableDefinition[] = [
  { name: '--color-bg-start', label: 'Background start', kind: 'color' },
  { name: '--color-bg-end', label: 'Background end', kind: 'color' },
  { name: '--color-text', label: 'Text', kind: 'color' },
  { name: '--color-muted', label: 'Muted text', kind: 'color' },
  { name: '--color-surface-1', label: 'Surface 1', kind: 'color' },
  { name: '--color-surface-2', label: 'Surface 2', kind: 'color' },
  { name: '--color-border', label: 'Border', kind: 'color' },
  { name: '--color-accent', label: 'Accent', kind: 'color' },
  { name: '--color-accent-soft', label: 'Soft accent', kind: 'color' },
  { name: '--color-card-background', label: 'Card background', kind: 'color' },
  { name: '--font-family-base', label: 'Base font family', kind: 'font-family' },
  { name: '--font-family-heading', label: 'Heading font family', kind: 'font-family' },
  { name: '--font-size-eyebrow', label: 'Eyebrow size', kind: 'font-size' },
  { name: '--font-size-title', label: 'Title size', kind: 'font-size' },
  { name: '--font-size-section-heading', label: 'Section heading size', kind: 'font-size' },
  { name: '--font-size-body', label: 'Body size', kind: 'font-size' },
  { name: '--font-size-question', label: 'Question size', kind: 'font-size' },
  { name: '--font-size-answer', label: 'Answer size', kind: 'font-size' },
  { name: '--font-size-score', label: 'Score size', kind: 'font-size' },
  { name: '--font-size-member', label: 'Member size', kind: 'font-size' },
  { name: '--font-size-current-player', label: 'Current player size', kind: 'font-size' }
];

const darkBlueColors: Record<string, string> = {
  '--color-bg-start': '#020617', '--color-bg-end': '#0f172a', '--color-text': '#f8fafc', '--color-muted': '#cbd5e1',
  '--color-surface-1': 'rgba(15, 23, 42, 0.82)', '--color-surface-2': 'rgba(30, 41, 59, 0.82)',
  '--color-border': 'rgba(153, 194, 159, 0.50)', '--color-accent': '#3b82f6', '--color-accent-soft': '#93c5fd',
  '--color-card-background': 'rgba(53, 94, 59, 0.20)'
};
const darkColors: Record<string, string> = {
  '--color-bg-start': '#09090b', '--color-bg-end': '#18181b', '--color-text': '#fafafa', '--color-muted': '#d4d4d8',
  '--color-surface-1': 'rgba(24, 24, 27, 0.9)', '--color-surface-2': 'rgba(39, 39, 42, 0.92)',
  '--color-border': 'rgba(113, 113, 122, 0.35)', '--color-accent': '#71717a', '--color-accent-soft': '#a1a1aa',
  '--color-card-background': 'rgba(39, 39, 42, 0.5)'
};
const grayBlackColors: Record<string, string> = {
  '--color-bg-start': '#000000', '--color-bg-end': '#111111', '--color-text': '#f5f5f5', '--color-muted': '#d4d4d4',
  '--color-surface-1': 'rgba(18, 18, 18, 0.92)', '--color-surface-2': 'rgba(32, 32, 32, 0.94)',
  '--color-border': 'rgba(163, 163, 163, 0.3)', '--color-accent': '#737373', '--color-accent-soft': '#a3a3a3',
  '--color-card-background': 'rgba(32, 32, 32, 0.5)'
};

const typographyDefaults: Record<string, string> = {
  '--font-family-base': 'Inter, Segoe UI, sans-serif', '--font-family-heading': 'Inter, Segoe UI, sans-serif',
  '--font-size-eyebrow': '0.75rem', '--font-size-title': '2rem', '--font-size-section-heading': '1.25rem',
  '--font-size-body': '1rem', '--font-size-question': '1.15rem', '--font-size-answer': '1rem',
  '--font-size-score': '3rem', '--font-size-member': '1rem', '--font-size-current-player': '1.25rem'
};

export function getThemeVariableDefaults(theme: PortalThemeKey): Record<string, string> {
  return { ...(theme === 'dark' ? darkColors : theme === 'gray-black' ? grayBlackColors : darkBlueColors), ...typographyDefaults };
}

export function validateThemeVariable(name: string, value: string): string | null {
  const definition = THEME_VARIABLE_DEFINITIONS.find(item => item.name === name);
  if (!definition) return 'This variable is not supported.';
  const trimmed = value.trim();
  if (!trimmed) return 'A value is required.';
  if (trimmed.length > 128) return 'Value must be 128 characters or fewer.';
  if (/[{}<>"';]|\/\*|\*\/|url\s*\(|https?:\/\/|\bimportant\b|:/.test(trimmed)) return 'Value contains unsupported CSS or unsafe content.';
  if (definition.kind === 'color' && !/^(#[0-9a-f]{3,8}|(?:rgb|hsl)a?\(\s*[\d.%\s,]+\)|color-mix\(\s*in\s+srgb\s*,\s*[^()]+\))$/i.test(trimmed)) return 'Use a hex, rgb/rgba, hsl/hsla, or color-mix color.';
  if (definition.kind === 'font-family' && !/^(?:[A-Za-z][A-Za-z0-9-]*\s*)+(?:,\s*(?:(?:[A-Za-z][A-Za-z0-9-]*\s*)+|sans-serif|serif|monospace|system-ui))*$/.test(trimmed)) return 'Use a safe comma-separated font family list.';
  if (definition.kind === 'font-size' && !/^(?:0|(?:\d+(?:\.\d+)?)(?:px|rem|em|%))(?:\s*\/\s*(?:\d+(?:\.\d+)?)(?:px|rem|em|%))?$|^clamp\(\s*[^()]+\)$/.test(trimmed)) return 'Use a bounded px, rem, em, %, or clamp() font size.';
  return null;
}

export function validateThemeVariables(values: Record<string, string>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const definition of THEME_VARIABLE_DEFINITIONS) {
    const value = values[definition.name];
    if (typeof value === 'string') {
      const error = validateThemeVariable(definition.name, value);
      if (error) errors[definition.name] = error;
    }
  }
  return errors;
}

export function colorPickerValue(value: string): string {
  const trimmed = value.trim();
  const hexMatch = trimmed.match(/^#([0-9a-f]{3,8})$/i);
  if (hexMatch) {
    const rawHex = hexMatch[1];
    const hex = rawHex.length === 4 ? rawHex.slice(0, 3) : rawHex.slice(0, 6);
    return hex.length === 3
      ? `#${hex.split('').map(character => character + character).join('')}`.toLowerCase()
      : `#${hex}`.toLowerCase();
  }

  const rgbMatch = trimmed.match(/^rgba?\(\s*([\d.]+)%?\s*,\s*([\d.]+)%?\s*,\s*([\d.]+)%?/i);
  if (rgbMatch) {
    const channels = rgbMatch.slice(1, 4).map(channel => {
      const numeric = Number(channel);
      return Math.round(numeric <= 1 ? numeric * 255 : numeric);
    });
    if (channels.every(channel => channel >= 0 && channel <= 255)) {
      return `#${channels.map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
    }
  }

  return '#000000';
}

export function colorTextColor(value: string): string {
  const hex = colorPickerValue(value).slice(1);
  const channels = [0, 2, 4].map(index => parseInt(hex.slice(index, index + 2), 16) / 255);
  const linear = channels.map(channel => channel <= 0.03928
    ? channel / 12.92
    : Math.pow((channel + 0.055) / 1.055, 2.4));
  const luminance = (0.2126 * linear[0]) + (0.7152 * linear[1]) + (0.0722 * linear[2]);
  return luminance > 0.179 ? '#111827' : '#ffffff';
}

export function formatPickedColor(value: string, existingValue: string): string {
  const hex = value.toLowerCase();
  const channels = [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16));
  const existing = existingValue.trim();
  const rgbaMatch = existing.match(/^rgba?\(\s*([\d.]+)%?\s*,\s*([\d.]+)%?\s*,\s*([\d.]+)%?(?:\s*,\s*([\d.]+))?\s*\)$/i);
  const hexAlphaMatch = existing.match(/^#(?:[\da-f]{3}|[\da-f]{6})([\da-f]{2})$/i);
  const shortHexAlphaMatch = existing.match(/^#([\da-f]){3}([\da-f])$/i);
  const alpha = rgbaMatch?.[4]
    ?? (hexAlphaMatch ? (parseInt(hexAlphaMatch[1], 16) / 255).toFixed(2) : undefined)
    ?? (shortHexAlphaMatch ? (parseInt(shortHexAlphaMatch[2].repeat(2), 16) / 255).toFixed(2) : '1');
  return `rgba(${channels.join(', ')}, ${alpha})`;
}
